'use client';
import { MoreNavigationItem } from '@/features/navigation/components/desktop-navigation/more-navigation-item';
import {
  MORE_BUTTON_FALLBACK_WIDTH,
  calculateOverflowingItemsCount,
  getNavigationItemsDimensions,
  navigationInitialState,
  navigationReducer,
  updateHelpButtonWidth,
  updateMoreButtonWidth,
} from '@/features/navigation/utils/desktop-navigation-reducer';
import {
  handleArrowDown,
  handleArrowUp,
  handleEscape,
} from '@/features/navigation/utils/keyboard-events';
import { useBreakpointUp } from '@/hooks/use-theme';
import type { Navigation } from '@/navigation/types/navigation';
import { Redirect, getRedirectPath } from '@/utils/redirects';
import { useEventListener, useKeyboardEvent } from '@react-hookz/web';
import { clsx } from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { memo, useLayoutEffect, useReducer, useRef } from 'react';
import { useFocusController } from 'react-focus-lock';
import { HeaderNavigationItem } from './header-navigation-item';
import { HeaderNavigationLink } from './header-navigation-link';

interface Props {
  menu: Navigation[];
}

const DesktopNavigation = ({ menu }: Props) => {
  const t = useTranslations('checkout');

  const ref = useRef<HTMLDivElement | null>(null);
  const helpButtonRef = useRef<HTMLLIElement | null>(null);
  const depthLevel = 0;
  const locale = useLocale();

  const { focusNext, focusPrev } = useFocusController(ref);
  const isDesktop = useBreakpointUp('l');

  const [
    {
      helpButtonWidth,
      isInitialOverflowHidden,
      moreButtonWidth,
      navigationItemDimensions,
      numberOfOverflowingElements,
    },
    dispatch,
  ] = useReducer(navigationReducer, navigationInitialState);

  const handleResize = () => {
    if (!ref.current || !helpButtonWidth || !isDesktop) return;
    const containerWidth = ref.current.getBoundingClientRect().width;
    if (containerWidth <= 0) return;

    calculateOverflowingItemsCount(
      containerWidth,
      navigationItemDimensions,
      moreButtonWidth,
      dispatch,
    );
  };

  useEventListener(
    typeof window != 'undefined' ? window : null,
    'resize',
    handleResize,
  );

  useLayoutEffect(() => {
    if (!ref?.current || !isDesktop || !isInitialOverflowHidden) return;

    const navItems = getNavigationItemsDimensions(
      ref.current.children,
      dispatch,
    );

    const moreButton = navItems.at(-1); // Last button = more Button

    updateMoreButtonWidth(moreButton?.width, dispatch);
    updateHelpButtonWidth(helpButtonRef, dispatch);

    calculateOverflowingItemsCount(
      ref.current.getBoundingClientRect().width,
      navItems,
      moreButton?.width || MORE_BUTTON_FALLBACK_WIDTH,
      dispatch,
    );

    dispatch({ type: 'SET_INITIAL_OVERFLOW_HIDDEN', value: false });
  }, [helpButtonWidth, helpButtonRef, isDesktop, isInitialOverflowHidden]);

  useKeyboardEvent(
    'ArrowDown',
    (e) => handleArrowDown(e, isDesktop, focusNext),
    [],
  );

  useKeyboardEvent(
    'ArrowUp',
    (e) => handleArrowUp(e, isDesktop, focusPrev),
    [],
  );

  useKeyboardEvent(
    'Escape',
    (e) => {
      handleEscape(e, isDesktop);
    },
    [],
  );

  return (
    <div
      className={clsx(
        'mt-xxs l:block hidden w-full px-l pt-xxs pb-xs group-[.sticky]:hidden xl:px-0 ',
      )}
    >
      <div className="relative z-100 w-full xl:container">
        {/* //15px = 12px padding of navigation button + 3px border of navigation button */}
        <ul
          className={clsx('relative flex w-full gap-3 ')}
          id="desktop-navigation"
        >
          <div
            className={clsx(
              'l:-ml-3 relative flex h-clickable w-full gap-3 transition-all xl:ml-[-15px]',
              isInitialOverflowHidden
                ? 'flex-wrap overflow-hidden'
                : 'flex-nowrap',
            )}
            ref={ref}
            data-testid={'desktop-navigation-list'}
            style={{
              width: helpButtonWidth
                ? `calc(100% - ${helpButtonWidth}px - 3px)`
                : '100%',
            }}
          >
            {(!numberOfOverflowingElements
              ? menu
              : menu?.slice(0, -numberOfOverflowingElements)
            )?.map((navItem) => (
              <HeaderNavigationItem
                key={navItem.id}
                depthLevel={depthLevel}
                item={navItem}
              />
            ))}
            <MoreNavigationItem
              depthLevel={depthLevel}
              data={menu}
              numberOfOverflowingElements={numberOfOverflowingElements}
              isInitialOverflowHidden={isInitialOverflowHidden}
            />
          </div>

          <HeaderNavigationLink
            href={getRedirectPath(Redirect.HELP, locale)}
            title={t('confirmation.btn_contact')}
            ref={helpButtonRef}
            className="xl:-mr-xxs l:mr-xxs ml-auto "
          />
        </ul>
      </div>
    </div>
  );
};

export default memo(DesktopNavigation);
